import { isNil } from 'lodash';
import { FieldRecord } from '@shared/scenes/entity/types';
import {
  getComponentLabelWithFallback,
  isStaticTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

/** Cap each field value so a single large field can't dominate the prompt. */
const MAX_VALUE_LENGTH = 240;

/**
 * Builds the prompt sent to the Ask gateway for a subform summary. The Ask agent
 * only receives a free-text prompt plus the card scope, so it cannot be told
 * "consider only this subform" through the scope — the instruction and the
 * subform's own field labels/values are embedded here instead. This keeps the
 * summary reliable for any subform regardless of which fields it has, and hedges
 * against the agent not isolating the subform on its own.
 *
 * The instruction block is static; only the template name and the current
 * (non-empty) field list are interpolated. Empty fields are omitted so the model
 * isn't asked to summarize nothing.
 */
export function buildSubformSummaryPrompt({
  workflowTemplate,
  orderedComponents,
  fields,
}: {
  workflowTemplate: API.WorkflowTemplateFragment | undefined;
  orderedComponents: API.AnyComponent[];
  fields: FieldRecord;
}): string {
  const subformName = workflowTemplate?.name?.trim() || 'this subform';
  const fieldLines = orderedComponents
    .filter(component => !isStaticTextComponent(component))
    .map(component => {
      const label = getComponentLabelWithFallback(component).trim();
      const value = fieldValueToString(fields[component.id]);
      if (isNil(value) || (isNil(label) && value === '')) {
        return null;
      }
      return `- ${label || 'Untitled field'}: ${value}`;
    })
    .filter((line): line is string => !isNil(line));

  const fieldBlock =
    fieldLines.length > 0 ? fieldLines.join('\n') : '(No field values have been entered yet.)';

  return [
    `You are summarizing ONE subform on a card in Coast. Consider ONLY the subform ` +
      `"${subformName}" and the field data listed below — ignore every other field on the card.`,
    '',
    'Subform fields (current values):',
    fieldBlock,
    '',
    'If recent change history for this subform is available to you, factor it in and call ' +
      'out notable recent changes; otherwise summarize the current state only.',
    '',
    'Write for a busy operator who is skimming. Include ONLY what is actionable or needs ' +
      'attention — anything failing, overdue, blocking, out of spec, unanswered, or otherwise ' +
      'concerning. For each such item, put the specific thing that needs attention in ' +
      '**bold**. Do NOT pad the summary with routine, healthy, or reassuring status just to ' +
      'fill space.',
    '',
    'If nothing needs attention, do not invent concerns — respond with a single bullet ' +
      'stating that everything looks on track and nothing needs attention right now.',
    '',
    'Start each bullet with a relevant emoji and separate the bullets with a blank line. ' +
      'Respond with NO MORE THAN 3 bullet points and nothing else — no preamble, no heading, ' +
      'no closing remark.',
  ].join('\n');
}

/**
 * Best-effort human-ish string for a field value. Uses the field's own JSON
 * representation (which is the plain string/number for the common text and number
 * fields) so this works across every field type without per-type logic. Returns
 * null when the field is missing or empty so the caller can drop the line.
 */
function fieldValueToString(field: FieldRecord[string] | undefined): string | null {
  if (isNil(field) || field.isEmpty) {
    return null;
  }

  let json: unknown;
  try {
    json = field.toFieldJson();
  } catch {
    return null;
  }

  if (isNil(json)) {
    return null;
  }

  const raw =
    typeof json === 'string' || typeof json === 'number' || typeof json === 'boolean'
      ? String(json)
      : JSON.stringify(json);

  const collapsed = raw.replace(/\s+/g, ' ').trim();
  if (collapsed === '') {
    return null;
  }
  return collapsed.length > MAX_VALUE_LENGTH
    ? `${collapsed.slice(0, MAX_VALUE_LENGTH)}…`
    : collapsed;
}
